/**
 * iter-386u-ad — Billing & Plans page.
 * Shows the two Razorpay tiers, current plan status, and one-click
 * subscribe flow with 7-day free-trial for Pro-Intelligence.
 */
import { useEffect, useState } from 'react';
import { PlanComparisonTable } from '@/components/billing/PlanComparisonTable';
import { DunningBanner } from '@/components/billing/DunningBanner';
import { ReceiptsHistory } from '@/components/billing/ReceiptsHistory';
import { PaymentsHistory } from '@/components/billing/PaymentsHistory';
import { buyCreditPack, startSubscription } from '@/lib/razorpayCheckout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlanCancelControls } from '@/components/billing/PlanCancelControls';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles, Zap, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { API } from '@/App';

// iter-386u-ba — Pricing UX polish: yearly toggle + currency switch.
const YEARLY_DISCOUNT = 0.17;               // 17% off vs. 12× monthly
const EXTRA_PROJECT_RATE = 1499;            // ₹ per extra project per month (all paid plans)
const FALLBACK_USD_PER_INR = 1 / 83;        // fallback when live FX is offline

const formatMoney = (rupees, currency, rate) => {
  const val = Number(rupees) || 0;
  if (currency === 'USD') {
    const usd = val * (rate || FALLBACK_USD_PER_INR);
    return `$${usd.toLocaleString('en-US', { maximumFractionDigits: usd < 100 ? 2 : 0 })}`;
  }
  return `\u20B9${val.toLocaleString('en-IN')}`;
};
const money = (n) => `\u20B9${(Number(n) || 0).toLocaleString('en-IN')}`;

// iter-386u-bb — Customer testimonials (static, curated). Rotate every ~6s.
const TESTIMONIALS = [
  { quote: "Bella Casa cut our BOQ turnaround from 3 days to 3 hours. The AI T&C generator alone paid for the plan in the first month.", author: 'Rhea Malhotra', role: 'Principal Designer, Studio Mango' },
  { quote: "Finally a tool that speaks contractor + client language. Vendor payouts + client billing sit in one ledger and I stopped double-entering everything.", author: 'Karan Shetty', role: 'Founder, Iyer & Shetty Architects' },
  { quote: "We switched from three separate spreadsheets to Bella Casa Enterprise last quarter. Charge-Own-Vendors alone recovers ₹40k a month.", author: 'Aditi Rao', role: 'Ops Head, House of Basalt' },
];

// iter-386u-bn — First-charge date now reads real anchor from backend
// (which returns Razorpay's `start_at` for active subs; otherwise today
// + trial_days). Falls back to today locally if backend call failed.
const formatChargeDate = (isoOrDate) => {
  const d = isoOrDate ? new Date(isoOrDate) : new Date();
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [keyId, setKeyId] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, sRes, cRes, nRes] = await Promise.all([
        axios.get(`${API}/billing/catalog`, { headers: authHeaders() }),
        axios.get(`${API}/billing/status`, { headers: authHeaders() }),
        axios.get(`${API}/billing/my-credits`, { headers: authHeaders() }).catch(() => ({ data: { balance: 0 } })),
        axios.get(`${API}/billing/next-charge-preview`, { headers: authHeaders() }).catch(() => ({ data: { plans: {} } })),
      ]);
      setPlans(pRes.data?.plans || []);
      setBundles(cRes.data?.can_topup === false ? [] : (pRes.data?.bundles || []));
      setWalletVisible(cRes.data?.wallet_visible !== false);
      setKeyId(pRes.data?.key_id || '');
      setStatus(sRes.data || null);
      setCreditBalance(cRes.data?.unlimited ? 1e9 : (cRes.data?.balance || 0));
      setChargePreview(nRes.data?.plans || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  };

  const [bundles, setBundles] = useState([]);
  const [walletVisible, setWalletVisible] = useState(true);
  const [creditBalance, setCreditBalance] = useState(0);
  // iter-386u-bn — real first-charge anchors per plan (from Razorpay when live)
  const [chargePreview, setChargePreview] = useState({});
  // iter-386u-ba — pricing UX toggles
  const [billingPeriod, setBillingPeriod] = useState('monthly');   // 'monthly' | 'yearly'
  const [currency, setCurrency] = useState('INR');                 // 'INR' | 'USD'
  // iter-386u-bj — per-user pricing slider (1-120 users)
  const [userCount, setUserCount] = useState(1);
  const [extraProjects, setExtraProjects] = useState(0);
  const pickUserTier = (tiers, n) => (tiers || []).find((t) => n >= t.min && n <= t.max) || (tiers || [])[0];
  // iter-386u-bb — live FX + testimonial rotator
  const [fxRate, setFxRate] = useState(FALLBACK_USD_PER_INR);
  const [fxSource, setFxSource] = useState('fallback');
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  useEffect(() => {
    axios.get(`${API}/billing/exchange-rate`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => {
        if (r.data?.usd_per_inr) {
          setFxRate(r.data.usd_per_inr);
          setFxSource(r.data.source || 'live');
        }
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const iv = setInterval(() => setTestimonialIdx((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(iv);
  }, []);

  const buyBundle = (bundle) => buyCreditPack(bundle, { onSuccess: load });

  useEffect(() => { load(); }, []);

  // Load Razorpay Checkout.js dynamically.
  useEffect(() => {
    if (document.getElementById('razorpay-checkout-js')) return;
    const s = document.createElement('script');
    s.id = 'razorpay-checkout-js';
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const [confirmation, setConfirmation] = useState(null);
  const subscribe = (plan) => {
    if (status?.billing_exempt) { toast('Owner access is permanent Enterprise — no billing needed.'); return; }
    setSubscribing(plan.code);
    startSubscription(plan, billingPeriod, {
      onSuccess: (res) => { setConfirmation({ ...res, plan_name: res.plan_name || plan.name }); load(); },
      onDone: () => setSubscribing(null),
    });
  };

  const isCurrent = (code) => status?.plan_code === code && ['active', 'trialing'].includes(status?.plan_status);
  // iter-386u-bl — resolve active plan's display name from plan catalog
  const activePlanName = (() => {
    if (!status?.plan_code || !['active', 'trialing'].includes(status?.plan_status)) return null;
    const p = plans.find((pp) => pp.code === status.plan_code);
    return p?.name || status.plan_code.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  })();

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto p-6 space-y-8" data-testid="billing-page">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Billing &amp; Plans</h1>
          {activePlanName ? (
            <p className="text-sm text-stone-600 mt-1" data-testid="billing-active-header">
              You are currently on the <span className="font-bold text-emerald-700">{activePlanName}</span> plan · upgrade any time — you&apos;re only billed the difference.
            </p>
          ) : (
            <p className="text-stone-500 mt-1">Pick the plan that matches your studio&apos;s rhythm. Cancel or upgrade any time.</p>
          )}
        </div>

        {status && (
          <div className="rounded-lg border border-stone-200 bg-white p-4 flex items-center justify-between" data-testid="billing-current-plan">
            <div>
              <div className="text-xs uppercase text-stone-500">Current plan</div>
              <div className="text-lg font-semibold text-stone-900 mt-1">
                {status.plan_code ? status.plan_code.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : 'No active plan'}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Status: <Badge variant="outline" className="ml-1">{status.plan_status || 'inactive'}</Badge>
                {status.plan_trial_ends_at && !status.billing_exempt && (
                  <span className="ml-2">Trial ends {new Date(status.plan_trial_ends_at).toLocaleDateString()}</span>
                )}
              </div>
              <PlanCancelControls status={status} onChanged={load} />
              <div className="mt-3"><DunningBanner dunning={status.dunning} /></div>
            </div>
            {walletVisible && <div className="text-right" data-testid="billing-credit-wallet">
              <div className="text-xs uppercase text-stone-500">AI Credits wallet</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="billing-credit-balance">
                {creditBalance >= 1e8 ? '\u221E' : creditBalance.toLocaleString('en-IN')}
              </div>
            </div>}
          </div>
        )}
        {status && (
          <div className="lg-glass rounded-2xl border p-5" data-testid="billing-history-tabs">
            <Tabs defaultValue="payments">
              <TabsList>
                <TabsTrigger value="payments" data-testid="payments-history-tab">Payments History</TabsTrigger>
                {!status.billing_exempt && <TabsTrigger value="receipts" data-testid="receipts-tab">Receipts</TabsTrigger>}
              </TabsList>
              <TabsContent value="payments" className="mt-4"><PaymentsHistory /></TabsContent>
              {!status.billing_exempt && <TabsContent value="receipts" className="mt-4"><ReceiptsHistory title="Your receipts" /></TabsContent>}
            </Tabs>
          </div>
        )}
        <Dialog open={!!confirmation} onOpenChange={(o) => { if (!o) setConfirmation(null); }}>
          <DialogContent data-testid="subscription-confirmation-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="text-emerald-600" /> You&apos;re on {confirmation?.plan_name}</DialogTitle>
              <DialogDescription>Payment received and your plan is active right now.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              {confirmation?.credited > 0 && <div data-testid="confirmation-credits">+{Number(confirmation.credited).toLocaleString('en-IN')} AI credits added to your wallet</div>}
              {confirmation?.balance != null && <div>Wallet balance: <b>{Number(confirmation.balance).toLocaleString('en-IN')}</b> credits</div>}
              <div className="text-stone-500">Your plan renews monthly and included credits refill on every renewal.</div>
            </div>
            <Button onClick={() => setConfirmation(null)} data-testid="confirmation-close-btn">Start using {confirmation?.plan_name}</Button>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="text-center text-stone-400 py-16">Loading plans…</div>
        ) : (
          <>
          {/* iter-386u-bb — Testimonial rotator (auto-cycles every 6s) */}
          <div className="rounded-2xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-5 md:p-6" data-testid="pricing-testimonial-strip">
            <div className="flex items-start gap-3">
              <Sparkles className="text-amber-500 flex-shrink-0 mt-1" size={18} />
              <div className="flex-1">
                <blockquote className="text-sm md:text-base text-stone-800 leading-relaxed italic">
                  &ldquo;{TESTIMONIALS[testimonialIdx].quote}&rdquo;
                </blockquote>
                <div className="mt-2 text-xs text-stone-500">
                  <span className="font-semibold text-stone-800">{TESTIMONIALS[testimonialIdx].author}</span>
                  <span className="mx-1.5 text-stone-300">·</span>
                  <span>{TESTIMONIALS[testimonialIdx].role}</span>
                </div>
              </div>
              <div className="hidden md:flex flex-col gap-1 pl-2">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTestimonialIdx(i)}
                    className={`w-1.5 h-1.5 rounded-full transition ${i === testimonialIdx ? 'bg-amber-500' : 'bg-stone-300 hover:bg-stone-400'}`}
                    aria-label={`Show testimonial ${i + 1}`}
                    data-testid={`testimonial-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* iter-386u-bj — Users slider — drives per-user rate on every card */}
          <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-3" data-testid="billing-users-slider">
            {/* iter-386u-bm — Quick-selection presets */}
            <div className="flex flex-wrap items-center gap-2" data-testid="team-size-presets">
              <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Quick pick</span>
              {[
                { label: 'Startup', users: 10, sub: '10 Users' },
                { label: 'Studio', users: 25, sub: '25 Users' },
                { label: 'Multi-Studio', users: 50, sub: '50+ Users' },
              ].map((preset) => {
                const isActive = userCount === preset.users;
                return (
                  <button
                    key={preset.label}
                    onClick={() => setUserCount(preset.users)}
                    className={`px-3 py-1.5 text-[11px] font-semibold rounded-full border transition ${
                      isActive
                        ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                        : 'bg-white border-stone-300 text-stone-700 hover:border-emerald-400 hover:text-emerald-700'
                    }`}
                    data-testid={`team-preset-${preset.label.toLowerCase().replace('-', '')}`}
                  >
                    <span className="font-bold">{preset.label}</span>
                    <span className={`ml-1.5 ${isActive ? 'text-emerald-100' : 'text-stone-400'}`}>({preset.sub})</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Team size</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-stone-900" data-testid="billing-users-count">{userCount}</span>
                  <span className="text-xs text-stone-500">users</span>
                </div>
                {userCount > 100 && (
                  <div className="mt-1 text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 inline-block" data-testid="enterprise-nudge">
                    Talk to us for volume &amp; SSO pricing →
                  </div>
                )}
              </div>
              <div className="flex-[3] min-w-0">
                <input type="range" min={1} max={120} value={userCount}
                  onChange={(e) => setUserCount(Number(e.target.value))}
                  className="w-full accent-emerald-600"
                  data-testid="billing-users-range" />
                <div className="mt-1 flex justify-between text-[10px] text-stone-400 font-mono">
                  <span>1</span><span>5</span><span>20</span><span>50</span><span>100+</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-4 pt-3 border-t border-stone-100" data-testid="billing-projects-slider">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Extra projects</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-stone-900" data-testid="billing-extra-projects-count">{extraProjects}</span>
                  <span className="text-xs text-stone-500">× {formatMoney(EXTRA_PROJECT_RATE, currency, fxRate)}/mo · paid plans</span>
                </div>
              </div>
              <div className="flex-[3] min-w-0">
                <input type="range" min={0} max={50} value={extraProjects}
                  onChange={(e) => setExtraProjects(Number(e.target.value))}
                  className="w-full accent-emerald-600"
                  data-testid="billing-extra-projects-range" />
                <div className="mt-1 flex justify-between text-[10px] text-stone-400 font-mono">
                  <span>0</span><span>10</span><span>25</span><span>50</span>
                </div>
              </div>
            </div>
          </div>

          {/* iter-386u-ba — Yearly/Monthly + currency toggles */}
          <div className="flex flex-wrap items-center justify-center gap-3" data-testid="pricing-toggles">
            <div className="inline-flex rounded-full border border-stone-200 bg-white p-1" role="tablist" aria-label="Billing period">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${billingPeriod === 'monthly' ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-800'}`}
                data-testid="toggle-monthly"
              >Monthly</button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-4 py-1.5 text-xs font-semibold rounded-full transition inline-flex items-center gap-1.5 ${billingPeriod === 'yearly' ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-800'}`}
                data-testid="toggle-yearly"
              >
                Yearly
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${billingPeriod === 'yearly' ? 'bg-emerald-400 text-stone-900' : 'bg-emerald-100 text-emerald-700'}`}>
                  Save {Math.round(YEARLY_DISCOUNT * 100)}%
                </span>
              </button>
            </div>
            <div className="inline-flex rounded-full border border-stone-200 bg-white p-1" role="tablist" aria-label="Currency">
              <button
                onClick={() => setCurrency('INR')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${currency === 'INR' ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-800'}`}
                data-testid="toggle-inr"
              >₹ INR</button>
              <button
                onClick={() => setCurrency('USD')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${currency === 'USD' ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-800'}`}
                data-testid="toggle-usd"
              >$ USD</button>
            </div>
          </div>

          <div className="grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {plans.map((p, idx) => {
              const isPro = p.code === 'pro_intelligence';
              const isEnterprise = p.code === 'enterprise';
              const active = isCurrent(p.code);
              // iter-386u-az — Incremental comparison: only show bullets NEW to
              // this tier. Reads dynamically from `permissions_bullets` so any
              // Admin Plan Center edit propagates instantly.
              const myBullets = (p.permissions_bullets && p.permissions_bullets.length) ? p.permissions_bullets : (p.inclusions || p.features || []);
              const prevPlan = idx > 0 ? plans[idx - 1] : null;
              const prevBullets = prevPlan ? ((prevPlan.permissions_bullets && prevPlan.permissions_bullets.length) ? prevPlan.permissions_bullets : (prevPlan.inclusions || prevPlan.features || [])) : [];
              const prevSet = new Set(prevBullets);
              const newBullets = idx === 0 ? myBullets : myBullets.filter((b) => !prevSet.has(b));
              const inheritLabel = prevPlan ? `${prevPlan.name} + everything below` : null;
              // iter-386u-bk — Incremental pricing math (Msg 892):
              // price = base_price + max(0, userCount - base_users) * extra_user_rate
              const basePrice = Number(p.base_price ?? p.amount) || 0;
              const baseUsers = Number(p.base_users ?? 1) || 1;
              const extraRate = Number(p.extra_user_rate ?? 0) || 0;
              const maxProjects = Number(p.max_projects ?? 0);
              const monthlyCredits = Number(p.monthly_credits ?? 0);
              const effectiveUsers = Math.max(userCount, baseUsers);
              const extraUsers = Math.max(0, effectiveUsers - baseUsers);
              const projectAddon = basePrice > 0 ? extraProjects * EXTRA_PROJECT_RATE : 0;
              const monthlyINR = basePrice + extraUsers * extraRate + projectAddon;
              const displayINR = billingPeriod === 'yearly'
                ? Math.round(monthlyINR * 12 * (1 - YEARLY_DISCOUNT))
                : monthlyINR;
              const periodSuffix = billingPeriod === 'yearly' ? 'yr' : 'mo';
              const yearlySavings = Math.round(monthlyINR * 12 * YEARLY_DISCOUNT);
              const nextPlan = plans[idx + 1];
              // For gap comparison also use incremental math on next plan
              const nextBase = nextPlan ? (Number(nextPlan.base_price ?? nextPlan.amount) || 0) : 0;
              const nextBaseUsers = nextPlan ? (Number(nextPlan.base_users ?? 1) || 1) : 1;
              const nextExtraRate = nextPlan ? (Number(nextPlan.extra_user_rate ?? 0) || 0) : 0;
              const nextEffUsers = nextPlan ? Math.max(userCount, nextBaseUsers) : 0;
              const nextMonthly = nextPlan ? (nextBase + Math.max(0, nextEffUsers - nextBaseUsers) * nextExtraRate + (nextBase > 0 ? extraProjects * EXTRA_PROJECT_RATE : 0)) : 0;
              const gapMonthly = nextPlan ? Math.max(0, nextMonthly - monthlyINR) : 0;
              // iter-386u-bl — Enterprise per-user savings vs Essential (live vs slider)
              let enterpriseSavingPerUser = 0;
              let showBestValueRibbon = false;
              if (isEnterprise) {
                const ess = plans.find((pl) => pl.code === 'essential');
                if (ess) {
                  const essBase = Number(ess.base_price ?? ess.amount) || 0;
                  const essBaseUsers = Number(ess.base_users ?? 1) || 1;
                  const essExtra = Number(ess.extra_user_rate ?? 0) || 0;
                  const essEff = Math.max(userCount, essBaseUsers);
                  const essMonthly = essBase + Math.max(0, essEff - essBaseUsers) * essExtra + (essBase > 0 ? extraProjects * EXTRA_PROJECT_RATE : 0);
                  const entPerUser = monthlyINR / Math.max(effectiveUsers, 1);
                  const essPerUser = essMonthly / Math.max(essEff, 1);
                  enterpriseSavingPerUser = Math.max(0, Math.round(essPerUser - entPerUser));
                }
                showBestValueRibbon = userCount > 30;
              }
              return (
                <div
                  key={p.code}
                  className={`rounded-2xl relative bg-white flex flex-col cursor-pointer transition overflow-hidden ${
                    active
                      ? 'border-4 border-emerald-600 shadow-xl shadow-emerald-100 ring-2 ring-emerald-200'
                      : isPro
                        ? 'border-2 border-emerald-500 shadow-lg shadow-emerald-100 hover:shadow-xl'
                        : 'border-2 border-stone-200 hover:border-stone-300'
                  }`}
                  onClick={() => setUserCount(baseUsers)}
                  data-testid={`plan-card-${p.code}`}
                >
                  {/* Solid emerald header bar behind plan name (active state only) */}
                  {active && (
                    <div className="bg-emerald-600 text-white px-5 py-2 flex items-center gap-2" data-testid={`plan-current-badge-${p.code}`}>
                      <Check size={16} strokeWidth={3} />
                      <span className="text-[11px] uppercase tracking-wider font-bold">Your Current Plan</span>
                    </div>
                  )}
                  {/* Best-Value ribbon for Enterprise when >30 users */}
                  {showBestValueRibbon && !active && (
                    <div className="bg-amber-500 text-white px-5 py-2 flex items-center gap-2" data-testid={`plan-recommended-teams-${p.code}`}>
                      <Sparkles size={14} />
                      <span className="text-[11px] uppercase tracking-wider font-bold">Recommended for Teams</span>
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    {isPro && !active && !showBestValueRibbon && (
                      <div className="absolute top-2 right-3 bg-emerald-600 text-white text-[10px] px-2 py-1 rounded-full uppercase tracking-wider font-semibold">
                        Most powerful
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      {isPro ? <Zap className="text-emerald-600" size={20} /> : <Check className="text-stone-500" size={20} />}
                      <h2 className={`text-xl text-stone-900 ${active ? 'font-extrabold' : 'font-bold'}`}>{p.name}</h2>
                    </div>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-stone-900" data-testid={`plan-price-${p.code}`}>
                        {formatMoney(displayINR, currency, fxRate)}
                      </span>
                      <span className="text-xs text-stone-500">/{periodSuffix}</span>
                    </div>
                    {/* Live math breakdown — updates as slider moves */}
                    {basePrice > 0 && (
                      <div className="text-[11px] text-stone-600 mt-1 font-medium" data-testid={`plan-formula-${p.code}`}>
                        <span className="text-emerald-700 font-bold">{baseUsers}</span>
                        <span className="text-stone-500"> included</span>
                        {extraUsers > 0 && (
                          <>
                            {' + '}
                            <span className="text-amber-700 font-bold">{extraUsers}</span>
                            <span className="text-stone-500"> extra @ </span>
                            <span className="font-bold text-stone-800">{formatMoney(extraRate, currency, fxRate)}</span>
                            <span className="text-stone-500">/mo</span>
                          </>
                        )}
                        {projectAddon > 0 && (
                          <span data-testid={`plan-formula-projects-${p.code}`}>
                            {' + '}
                            <span className="text-sky-700 font-bold">{extraProjects}</span>
                            <span className="text-stone-500"> project{extraProjects !== 1 ? 's' : ''} @ </span>
                            <span className="font-bold text-stone-800">{formatMoney(EXTRA_PROJECT_RATE, currency, fxRate)}</span>
                            <span className="text-stone-500">/mo</span>
                          </span>
                        )}
                      </div>
                    )}
                    {billingPeriod === 'yearly' && monthlyINR > 0 && (
                      <div className="mt-2 rounded-lg border-2 border-emerald-400 bg-emerald-50 p-2.5" data-testid={`plan-savings-yearly-${p.code}`}>
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="text-emerald-600 flex-shrink-0" size={13} />
                          <span className="text-[13px] font-extrabold text-emerald-800">
                            Save {formatMoney(yearlySavings, currency, fxRate)}/yr
                          </span>
                        </div>
                        <div className="text-[10px] text-emerald-700 mt-0.5 leading-tight">
                          vs {formatMoney(monthlyINR, currency, fxRate)}/mo billed monthly
                        </div>
                        <div className="text-[10px] text-stone-600 mt-1 leading-tight border-t border-emerald-200 pt-1">
                          First charge: <span className="font-semibold">{formatChargeDate(chargePreview?.[p.code]?.first_charge_at)}</span>
                        </div>
                      </div>
                    )}
                    {/* iter-386u-bl — Enterprise dynamic per-user savings badge */}
                    {isEnterprise && enterpriseSavingPerUser > 0 && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 self-start" data-testid={`plan-savings-per-user-${p.code}`}>
                        <Sparkles size={11} />
                        Save {formatMoney(enterpriseSavingPerUser, currency, fxRate)} per user!
                      </div>
                    )}
                    {gapMonthly > 0 && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 self-start" data-testid={`plan-upgrade-gap-${p.code}`}>
                        <Sparkles size={10} />
                        Only {formatMoney(gapMonthly, currency, fxRate)}/mo less than {nextPlan.name}
                      </div>
                    )}
                    {p.trial_days > 0 && !active && (
                      <div className="text-[11px] text-emerald-700 mt-2 font-semibold">
                        {p.trial_days}-day free trial
                      </div>
                    )}
                    <p className="text-xs text-stone-500 mt-3 leading-snug min-h-[36px]">{p.description}</p>

                    {/* iter-386u-bl — Plan Limits block: Users / Projects / AI Credits */}
                    {/* iter-386u-bn — Wider padding so 5,000 / 500 never touch borders */}
                    <div className="mt-4 grid grid-cols-3 gap-2 p-3 rounded-xl bg-stone-50 border border-stone-200" data-testid={`plan-limits-${p.code}`}>
                      <div className="text-center px-4 py-2 rounded-lg bg-white border border-stone-200">
                        <div className="text-sm md:text-base font-extrabold text-stone-900 leading-tight whitespace-nowrap" data-testid={`plan-limit-users-${p.code}`}>{baseUsers}</div>
                        <div className="text-[9px] uppercase text-stone-500 leading-tight tracking-tight mt-0.5">Users</div>
                      </div>
                      <div className="text-center px-4 py-2 rounded-lg bg-white border border-stone-200">
                        <div className="text-sm md:text-base font-extrabold text-stone-900 leading-tight whitespace-nowrap" data-testid={`plan-limit-projects-${p.code}`}>{maxProjects > 0 ? (maxProjects + (projectAddon > 0 ? extraProjects : 0)).toLocaleString('en-IN') : '\u2014'}</div>
                        <div className="text-[9px] uppercase text-stone-500 leading-tight tracking-tight mt-0.5">Projects</div>
                      </div>
                      <div className="text-center px-4 py-2 rounded-lg bg-white border border-stone-200">
                        <div className="text-sm md:text-base font-extrabold text-stone-900 leading-tight whitespace-nowrap" data-testid={`plan-limit-credits-${p.code}`}>{monthlyCredits.toLocaleString('en-IN')}</div>
                        <div className="text-[9px] uppercase text-stone-500 leading-tight tracking-tight mt-0.5">AI/mo</div>
                      </div>
                    </div>

                    {inheritLabel && (
                      <div className="mt-4 px-2.5 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-[11px] text-stone-600 font-medium" data-testid={`plan-inherit-${p.code}`}>
                        <span className="text-stone-400">↑ </span>{inheritLabel}
                      </div>
                    )}

                    <ul className="mt-4 space-y-2 flex-1" data-testid={`plan-bullets-${p.code}`}>
                      {newBullets.length === 0 && inheritLabel && (
                        <li className="text-[11px] text-stone-400 italic">No exclusive extras — pure inheritance from {prevPlan.name}.</li>
                      )}
                      {newBullets.map((f) => (
                        <li key={f} className={`flex items-start gap-2 text-xs ${inheritLabel ? 'text-emerald-900 font-medium' : 'text-stone-700'}`} data-testid={`plan-bullet-${p.code}-${f.replace(/\s+/g, '-').toLowerCase()}`}>
                          {inheritLabel ? (
                            <Sparkles className="text-amber-500 flex-shrink-0 mt-0.5" size={13} />
                          ) : (
                            <Check className="text-emerald-600 flex-shrink-0 mt-0.5" size={13} />
                          )}
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full mt-5 ${active ? 'bg-emerald-700 hover:bg-emerald-700 cursor-not-allowed opacity-90' : isPro ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                      variant={active ? 'default' : (isPro ? 'default' : 'outline')}
                      size="sm"
                      disabled={subscribing === p.code || active || status?.billing_exempt || (p.code === 'free' && status?.plan_code && status.plan_code !== 'free' && status?.plan_status === 'active')}
                      onClick={(e) => { e.stopPropagation(); subscribe(p); }}
                      data-testid={`plan-subscribe-${p.code}`}
                    >
                      {active
                        ? 'Current Plan'
                        : status?.billing_exempt
                          ? 'Owner access'
                          : (p.code === 'free' && status?.plan_code && status.plan_code !== 'free' && status?.plan_status === 'active')
                            ? 'Included in your plan'
                        : subscribing === p.code
                          ? 'Opening checkout…'
                          : p.trial_days > 0
                            ? `Start ${p.trial_days}-day trial`
                            : 'Subscribe'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* iter-386u-bn — "Compare Plans" opens the feature matrix in a modal */}
          <div className="pt-2 text-center">
            <Dialog>
              <DialogTrigger asChild>
                <button
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-stone-900 hover:bg-stone-800 rounded-full px-6 py-2.5 transition shadow-sm"
                  data-testid="feature-matrix-toggle"
                >
                  <Sparkles size={14} />
                  Compare Plans
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl w-[92vw] max-h-[90vh] overflow-y-auto" data-testid="compare-plans-modal">
                <DialogHeader>
                  <DialogTitle className="text-2xl">Compare all plans</DialogTitle>
                  <DialogDescription>
                    Side-by-side of every feature across Free, Essential, Pro-Intelligence and Enterprise.
                  </DialogDescription>
                </DialogHeader>
                <FeatureMatrix plans={plans} />
              </DialogContent>
            </Dialog>
          </div>
          </>
        )}
        <p className="text-[11px] text-stone-400 text-center">
          Payments processed securely by Razorpay{String(keyId).startsWith('rzp_test') ? ' (test mode)' : ''}. Invoices are emailed and available under Payments History after each successful charge.
        </p>

        {/* iter-386u-af — Credit top-up bundles (dynamic from DB) */}
        {bundles.length > 0 && !status?.billing_exempt && (
          <div className="pt-6" data-testid="billing-bundles-section">
            <h2 className="text-2xl font-bold text-stone-900 mb-1">Credit Top-ups</h2>
            <p className="text-sm text-stone-500 mb-4">Buy credits any time to keep your AI tools running.</p>
            <div className="grid md:grid-cols-3 gap-4">
              {bundles.map((b) => (
                <div key={b.code} className="rounded-xl border-2 border-stone-200 bg-white p-5" data-testid={`bundle-card-${b.code}`}>
                  <div className="text-sm text-stone-500">{b.name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-stone-900" data-testid={`bundle-price-${b.code}`}>{formatMoney(b.amount, currency, fxRate)}</span>
                  </div>
                  <div className="mt-1 text-sm text-emerald-700 font-semibold">
                    {b.credits.toLocaleString('en-IN')} credits
                  </div>
                  <Button className="w-full mt-4 bg-sky-600 hover:bg-sky-700" onClick={() => buyBundle(b)} data-testid={`bundle-buy-${b.code}`}>
                    Buy now
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* iter-386u-ah — Marketplace add-ons + Template store (credit-billed) */}
        <MarketplaceSection onChanged={load} />
        <TemplateStoreSection onChanged={load} />

        {/* iter-386u-ae — Owner-only Grandfather card. */}
        <GrandfatherCard />

        <PlanComparisonTable
          plans={plans}
          currentCode={status?.plan_code && ['active', 'trialing'].includes(status?.plan_status) ? status.plan_code : 'free'}
          onUpgrade={subscribe}
          subscribing={subscribing}
        />
      </div>
    </Layout>
  );
}


// ─── Grandfather management card (Owner-only) ────────────────────────
function GrandfatherCard() {
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [pick, setPick] = useState('');
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  // iter-386u-ai — pick which plan to grant (Free/Essential/Pro/Enterprise).
  const [plans, setPlans] = useState([]);
  const [grantPlan, setGrantPlan] = useState('pro_intelligence');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    try {
      const meResp = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
      const meDoc = meResp.data || {};
      setMe(meDoc);
      if ((meDoc.role || '') !== 'owner') return;
      const [gf, el, cat] = await Promise.all([
        axios.get(`${API}/billing/grandfathered`, { headers: authHeaders() }),
        axios.get(`${API}/billing/eligible-users`, { headers: authHeaders() }),
        axios.get(`${API}/billing/catalog`, { headers: authHeaders() }),
      ]);
      setRows(gf.data?.users || []);
      setEligible(el.data?.users || []);
      setPlans(cat.data?.plans || []);
    } catch (e) {
      // silently skip — non-owners simply don't see this card
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (userId, enable, name) => {
    setSaving(true);
    try {
      await axios.post(`${API}/billing/grandfather`, { user_id: userId, enable }, { headers: authHeaders() });
      toast.success(enable ? `${name || 'User'} now has permanent Pro access` : 'Grandfather access revoked');
      setPick('');
      setQ('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  // iter-386u-ai — grant a specific plan (does NOT set grandfathered_pro; user
  // will have a normal plan_status which respects trial_days / caps).
  const grantSpecificPlan = async (userId, name) => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/billing/grant-plan`,
        { user_id: userId, plan_code: grantPlan },
        { headers: authHeaders() },
      );
      toast.success(`${name || 'User'} granted ${r.data?.plan_name || grantPlan}`);
      setQ('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grant failed');
    } finally {
      setSaving(false);
    }
  };

  if (!me || me.role !== 'owner') return null;

  const filteredEligible = eligible
    .filter((u) => {
      if (!q) return true;
      const s = `${u.name || ''} ${u.email || ''} ${u.role || ''}`.toLowerCase();
      return s.includes(q.toLowerCase());
    })
    .slice(0, 8);

  return (
    <div className="mt-6 rounded-2xl border-2 border-dashed border-stone-300 bg-white p-6" data-testid="grandfather-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-lg font-bold text-stone-900">Founding-account access</h3>
          <p className="text-xs text-stone-500 mt-1">
            Only visible to you as the studio owner. Grants permanent Pro-Intelligence access to specific accounts — they never see a paywall or a plan card.
          </p>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-900 text-white uppercase tracking-wider">Owner</span>
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-wider text-stone-500">Grant to a user</label>
        <div className="flex gap-2 mt-1 flex-wrap">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email or role…"
            className="flex-1 min-w-[240px] h-9 border border-stone-300 rounded px-2 text-sm"
            data-testid="grandfather-search"
          />
          <select
            value={grantPlan}
            onChange={(e) => setGrantPlan(e.target.value)}
            className="h-9 border border-stone-300 rounded px-2 text-sm bg-white"
            data-testid="grandfather-plan-picker"
          >
            {plans.map((p) => (
              <option key={p.code} value={p.code}>Grant · {p.name}</option>
            ))}
          </select>
        </div>
        <div className="mt-2 divide-y divide-stone-100 border border-stone-200 rounded" data-testid="grandfather-eligible-list">
          {filteredEligible.length === 0 ? (
            <div className="p-3 text-xs text-stone-400 text-center">
              {q ? 'No match. Try another query.' : 'All eligible users are already grandfathered.'}
            </div>
          ) : filteredEligible.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2 px-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-stone-800 truncate">{u.name || '—'} <span className="text-[11px] text-stone-400 ml-1 uppercase">{u.role}</span></div>
                <div className="text-[11px] text-stone-500 truncate">{u.email}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-sky-700 border-sky-300 hover:bg-sky-50"
                  disabled={saving}
                  onClick={() => grantSpecificPlan(u.id, u.name || u.email)}
                  data-testid={`grandfather-grant-plan-${u.id}`}
                >Grant {plans.find(p => p.code === grantPlan)?.name || 'plan'}</Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  disabled={saving}
                  onClick={() => toggle(u.id, true, u.name || u.email)}
                  data-testid={`grandfather-add-${u.id}`}
                >Grant lifetime Pro</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <label className="text-xs uppercase tracking-wider text-stone-500">Currently grandfathered ({rows.length})</label>
        <div className="mt-1 divide-y divide-stone-100 border border-stone-200 rounded" data-testid="grandfathered-list">
          {rows.length === 0 ? (
            <div className="p-3 text-xs text-stone-400 text-center">No one is grandfathered yet.</div>
          ) : rows.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2 px-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-stone-800 truncate">{u.name || '—'} <span className="text-[11px] text-emerald-700 ml-1 uppercase font-semibold">Pro · lifetime</span></div>
                <div className="text-[11px] text-stone-500 truncate">{u.email} · since {u.grandfathered_pro_at ? new Date(u.grandfathered_pro_at).toLocaleDateString() : '—'}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="text-rose-700 border-rose-300 hover:bg-rose-50"
                disabled={saving}
                onClick={() => toggle(u.id, false, u.name || u.email)}
                data-testid={`grandfather-remove-${u.id}`}
              >Revoke</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Marketplace add-ons (credit-billed) ─────────────────────────────
function MarketplaceSection({ onChanged }) {
  const [addons, setAddons] = useState([]);
  const [busy, setBusy] = useState(null);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/marketplace/addons`, { headers: authHeaders() });
      setAddons(r.data?.addons || []);
    } catch (e) { /* silent — endpoint optional */ }
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (a) => {
    setBusy(a.code);
    try {
      const r = await axios.post(`${API}/marketplace/addons/subscribe`, { code: a.code }, { headers: authHeaders() });
      toast.success(`${a.name} unlocked · expires ${new Date(r.data?.expires_at).toLocaleDateString()}`);
      load();
      onChanged && onChanged();
    } catch (e) {
      // 402 will already fire the global insufficient-credits modal
      if (e.response?.status !== 402) toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Subscribe failed');
    } finally { setBusy(null); }
  };

  if (!addons.length) return null;
  return (
    <div className="pt-6" data-testid="marketplace-section">
      <h2 className="text-2xl font-bold text-stone-900 mb-1">Add-ons Marketplace</h2>
      <p className="text-sm text-stone-500 mb-4">Extend your studio with focused tools. Billed from your credit balance · 30-day access.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {addons.map((a) => (
          <div key={a.code} className={`rounded-xl border-2 p-5 bg-white ${a.owned ? 'border-emerald-500' : 'border-stone-200'}`} data-testid={`addon-card-${a.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-stone-900">{a.name}</div>
                <div className="text-xs text-stone-500 mt-1 max-w-md">{a.description}</div>
              </div>
              {a.owned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase">Active</span>}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm text-stone-700"><span className="font-bold text-stone-900">{(a.credits_cost || a.amount || 0).toLocaleString('en-IN')}</span> credits / month</div>
              <Button
                size="sm"
                variant={a.owned ? 'outline' : 'default'}
                disabled={busy === a.code}
                onClick={() => subscribe(a)}
                data-testid={`addon-subscribe-${a.code}`}
              >{a.owned ? 'Renew' : busy === a.code ? '…' : 'Subscribe'}</Button>
            </div>
            {a.owned && a.expires_at && (
              <div className="text-[11px] text-stone-500 mt-2">Expires {new Date(a.expires_at).toLocaleDateString()}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Premium BOQ Template Store (one-time credit spend) ──────────────
function TemplateStoreSection({ onChanged }) {
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(null);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/template-store`, { headers: authHeaders() });
      setTemplates(r.data?.templates || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const buy = async (t) => {
    setBusy(t.id);
    try {
      await axios.post(`${API}/template-store/${t.id}/purchase`, {}, { headers: authHeaders() });
      toast.success(`${t.name} unlocked — you can now clone it in any BOQ.`);
      load();
      onChanged && onChanged();
    } catch (e) {
      if (e.response?.status !== 402) toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Purchase failed');
    } finally { setBusy(null); }
  };

  if (!templates.length) return null;
  return (
    <div className="pt-6" data-testid="template-store-section">
      <h2 className="text-2xl font-bold text-stone-900 mb-1">Premium BOQ Templates</h2>
      <p className="text-sm text-stone-500 mb-4">One-time purchase · lifetime access. Ready-to-use battle-tested BOQ scopes.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {templates.map((t) => (
          <div key={t.id} className={`rounded-xl border-2 p-5 bg-white ${t.owned ? 'border-emerald-500' : 'border-stone-200'}`} data-testid={`template-card-${t.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-stone-900">{t.name}</div>
                <div className="text-xs text-stone-500 mt-1 max-w-md">{t.description}</div>
              </div>
              {t.owned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase">Owned</span>}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm text-stone-700"><span className="font-bold text-stone-900">{(t.credits_cost || t.amount || 0).toLocaleString('en-IN')}</span> credits · one-time</div>
              <Button
                size="sm"
                disabled={t.owned || busy === t.id}
                onClick={() => buy(t)}
                data-testid={`template-buy-${t.code}`}
              >{t.owned ? 'Owned' : busy === t.id ? '…' : 'Buy'}</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// iter-386u-ba — Full feature matrix: side-by-side ✓ / — grid so power buyers
// can compare every capability across every tier. Reads permissions_bullets
// straight from /billing/catalog so any Plan Center edit propagates live.
function FeatureMatrix({ plans }) {
  // Build the master row list: every unique bullet across every plan, ordered by
  // the tier that first introduces it (Free → Essential → Pro → Enterprise).
  const rows = [];
  const seen = new Set();
  plans.forEach((p, idx) => {
    const bullets = (p.permissions_bullets && p.permissions_bullets.length) ? p.permissions_bullets : (p.inclusions || p.features || []);
    for (const b of bullets) {
      if (seen.has(b)) continue;
      seen.add(b);
      rows.push({ label: b, introducedIn: idx });
    }
  });
  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="feature-matrix">
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="bg-stone-100 border-b-2 border-stone-300 shadow-sm">
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-stone-600 font-semibold bg-stone-100">Feature</th>
              {plans.map((p) => (
                <th key={p.code} className="text-center px-4 py-3 text-[11px] uppercase tracking-wider text-stone-600 font-semibold whitespace-nowrap bg-stone-100">{p.name}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((row) => (
              <tr key={row.label} className="hover:bg-stone-50/50">
                <td className="px-4 py-2.5 text-stone-800">{row.label}</td>
                {plans.map((p, colIdx) => {
                  const bullets = (p.permissions_bullets && p.permissions_bullets.length) ? p.permissions_bullets : (p.inclusions || p.features || []);
                  const has = bullets.includes(row.label);
                  const isFirst = colIdx === row.introducedIn && has;
                  return (
                    <td key={p.code} className="px-4 py-2.5 text-center" data-testid={`matrix-${p.code}-${row.label.replace(/\s+/g, '-').toLowerCase()}`}>
                      {has ? (
                        isFirst ? <Sparkles className="inline text-amber-500" size={14} /> : <Check className="inline text-emerald-600" size={14} />
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-stone-100 text-[10px] text-stone-400 bg-stone-50/50">
        <Sparkles size={9} className="inline text-amber-500 mr-1" /> First tier that includes this feature · <Check size={9} className="inline text-emerald-600 mx-1" /> Available on this plan
      </div>
    </div>
  );
}
